import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { closeModal } from '../../redux/features/modalSlice';
import { useUpdateEventMutation } from '../../redux/services/eventService/event.Services';
import { successToast, errorToast } from '../../Utils/toast';

function EditEvents({ data }) {
  const dispatch = useDispatch();
  const [updateEvent, { isLoading }] = useUpdateEventMutation();
  
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    venue: '',
    capacity: '',
    status: 'upcoming',
    image: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

  useEffect(() => {
    if (data) {
      // Format dates for datetime-local input
      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().slice(0, 16);
      };

      setFormData({
        id: data.id || '',
        title: data.title || '',
        description: data.description || '',
        start_date: formatDate(data.start_date),
        end_date: formatDate(data.end_date),
        venue: data.venue || '',
        capacity: data.capacity || '',
        status: data.status || 'upcoming',
        image: null
      });
      setExistingImage(data.image_url || null); // This is now the full URL from backend
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      errorToast('Event title is required');
      return;
    }
    if (!formData.start_date) {
      errorToast('Start date is required');
      return;
    }
    if (!formData.end_date) {
      errorToast('End date is required');
      return;
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('id', formData.id);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('start_date', formData.start_date);
      formDataToSend.append('end_date', formData.end_date);
      formDataToSend.append('venue', formData.venue);
      formDataToSend.append('capacity', formData.capacity);
      formDataToSend.append('status', formData.status);
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      const response = await updateEvent(formDataToSend).unwrap();
      successToast(response?.message || 'Event updated successfully');
      dispatch(closeModal());
    } catch (error) {
      console.error('Update error:', error);
      errorToast(error?.data?.message || 'Failed to update event');
    }
  };

  return (
    <div className="edit-event-container">
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label fw-bold">
            Event Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter event title"
            required
            autoFocus
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter event description"
            rows="3"
          />
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">
              Start Date <span className="text-danger">*</span>
            </label>
            <input
              type="datetime-local"
              className="form-control"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">
              End Date <span className="text-danger">*</span>
            </label>
            <input
              type="datetime-local"
              className="form-control"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Venue</label>
            <input
              type="text"
              className="form-control"
              name="venue"
              value={formData.venue}
              onChange={handleChange}
              placeholder="Enter venue"
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Capacity</label>
            <input
              type="number"
              className="form-control"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              placeholder="Enter capacity"
              min="0"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Status</label>
          <select
            className="form-control"
            name="status"
            value={formData.status}
            onChange={handleChange}
          >
            <option value="upcoming">Upcoming</option>
            <option value="ongoing">Ongoing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Current Image */}
        {existingImage && !previewImage && (
          <div className="mb-3">
            <label className="form-label fw-bold">Current Image</label>
            <div>
              <img 
                src={existingImage} // FIXED: Use full URL from backend
                alt="Current Event" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                className="img-thumbnail"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/placeholder-image.png';
                }}
              />
            </div>
          </div>
        )}

        {/* Image Upload */}
        <div className="mb-3">
          <label className="form-label fw-bold">Change Image</label>
          <input
            type="file"
            className="form-control"
            name="image"
            onChange={handleFileChange}
            accept="image/*"
          />
        </div>

        {/* Image Preview */}
        {previewImage && (
          <div className="mb-3">
            <label className="form-label fw-bold">New Image Preview</label>
            <div>
              <img 
                src={previewImage} 
                alt="New Event Preview" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                className="img-thumbnail"
              />
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => dispatch(closeModal())}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditEvents;